/**
 * Queue realtime sync: validates backend queue WebSocket payloads and schedules
 * a single debounced, backend-authoritative refresh (custom DOM event).
 * Does not open sockets — routing is wired from websocketService.handleMessage.
 */

import { extractUserInfo } from '../utils/jwtDecoder';
import {
  getBooleanEnv,
  getNumberEnv,
  logRealtimeEnvStartup,
} from '../utils/runtimeEnv';

export const QUEUE_REALTIME_EVENT_TYPES = new Set([
  'TICKET_CREATED',
  'TICKET_TEAM_TRANSFERRED',
  'TICKET_ASSIGNED',
  'TICKET_REASSIGNED',
  'TICKET_UNASSIGNED',
  'TICKET_REOPENED',
  'TICKET_STATUS_CHANGED',
]);

const LOG_NS = 'queue-realtime';

export const QUEUE_REALTIME_SYNC_ENABLED =
  getBooleanEnv('REACT_APP_QUEUE_REALTIME_SYNC_ENABLED', true);

const DEBOUNCE_MS = getNumberEnv('REACT_APP_QUEUE_REALTIME_DEBOUNCE_MS', 650, 0);

logRealtimeEnvStartup({
  featureEnabled: QUEUE_REALTIME_SYNC_ENABLED,
  debounceMs: DEBOUNCE_MS,
});

let debounceTimer = null;
let debounceGeneration = 0;

function logStructured(level, stage, fields) {
  const line = {
    ns: LOG_NS,
    stage,
    ts: new Date().toISOString(),
    ...fields,
  };
  const msg = `[${LOG_NS}] ${JSON.stringify(line)}`;
  if (level === 'warn') {
    console.warn(msg);
  } else if (level === 'error') {
    console.error(msg);
  } else {
    //console.log(msg);
  }
}

export function isQueueRealtimePayload(message) {
  if (!message || typeof message !== 'object') {
    return false;
  }
  return (
    message.schemaVersion === 'v1' &&
    typeof message.eventType === 'string' &&
    QUEUE_REALTIME_EVENT_TYPES.has(message.eventType)
  );
}

function normalizeLong(v) {
  if (v == null || v === '') {
    return null;
  }
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

function getJwtTenantId() {
  const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
  if (!token) {
    return null;
  }
  const info = extractUserInfo(token);
  return normalizeLong(info?.tenantId);
}

function getSessionTeamIds() {
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (!raw) {
      return [];
    }
    const u = JSON.parse(raw);
    const tid = normalizeLong(u?.teamId ?? u?.team_id);
    return tid != null ? [tid] : [];
  } catch {
    return [];
  }
}

function getSessionUserId() {
  const fromStorage = normalizeLong(sessionStorage.getItem('userId') || sessionStorage.getItem('user_id'));
  if (fromStorage != null) {
    return fromStorage;
  }
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (!raw) {
      return null;
    }
    const u = JSON.parse(raw);
    return normalizeLong(u?.userId ?? u?.user_id ?? u?.id);
  } catch {
    return null;
  }
}

/** When backend sends explicit targetUserIds, honor that over legacy team audience. */
function userIsExplicitTargetRecipient(payload) {
  const targets = payload?.targetUserIds;
  if (!Array.isArray(targets) || targets.length === 0) {
    return null;
  }
  const myId = getSessionUserId();
  if (myId == null) {
    return false;
  }
  return targets.map(normalizeLong).some((id) => id === myId);
}

function tenantMatchesPayload(payload) {
  const jwtTid = getJwtTenantId();
  const evtTid = normalizeLong(payload.tenantId);
  if (evtTid == null) {
    logStructured('warn', 'queue_event_ignored', { reason: 'missing_event_tenant' });
    return false;
  }
  if (jwtTid == null) {
    logStructured('warn', 'queue_event_ignored', { reason: 'missing_jwt_tenant' });
    return false;
  }
  if (jwtTid !== evtTid) {
    logStructured('log', 'queue_event_ignored', { reason: 'tenant_mismatch', jwtTid, evtTid });
    return false;
  }
  return true;
}

function userTouchesAffectedTeams(payload) {
  const affected = payload.affectedTeamIds;
  if (!Array.isArray(affected) || affected.length === 0) {
    return true;
  }
  const nums = affected.map(normalizeLong).filter((n) => n != null);
  if (nums.length === 0) {
    return true;
  }
  const my = getSessionTeamIds();
  if (my.length === 0) {
    return true;
  }
  return my.some((t) => nums.includes(t));
}

/**
 * Paths where a silent queue-related refresh is appropriate.
 */
export function isQueueRelatedPath(pathname) {
  const p = pathname || '';
  if (
    p.startsWith('/admin/reports/list-of-tickets') ||
    p.startsWith('/reports/list-of-tickets')
  ) {
    return true;
  }
  if (!p.startsWith('/agent')) {
    return false;
  }
  if (p.startsWith('/agent/dashboard')) {
    return true;
  }
  if (p === '/agent/tickets' || p.startsWith('/agent/tickets/unassigned')) {
    return true;
  }
  if (p.startsWith('/agent/tickets/team-tickets')) {
    return true;
  }
  if (p.startsWith('/agent/tickets/my-tickets')) {
    return true;
  }
  if (p.startsWith('/agent/tickets/escalated')) {
    return true;
  }
  if (p.startsWith('/agent/tickets/closed')) {
    return true;
  }
  if (p.startsWith('/agent/tickets/assignment')) {
    return true;
  }
  return false;
}

export function clearQueueRealtimeDebounce() {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

/**
 * Global notification relevance check (no screen/path gating).
 * Used by NotificationContext-triggering flow so bell/toast remain page-independent.
 */
export function isQueueRealtimeRelevantForCurrentUser(message) {
  if (!QUEUE_REALTIME_SYNC_ENABLED) {
    logStructured('log', 'queue_global_event_ignored', { reason: 'feature_disabled' });
    return false;
  }
  if (!isQueueRealtimePayload(message)) {
    logStructured('log', 'queue_global_event_ignored', { reason: 'not_queue_payload' });
    return false;
  }
  if (!tenantMatchesPayload(message)) {
    return false;
  }
  const explicitTarget = userIsExplicitTargetRecipient(message);
  if (explicitTarget === true) {
    return true;
  }
  if (explicitTarget === false) {
    logStructured('log', 'queue_global_event_ignored', {
      reason: 'user_not_in_targetUserIds',
      targetUserIds: message.targetUserIds,
    });
    return false;
  }
  if (!userTouchesAffectedTeams(message)) {
    logStructured('log', 'queue_global_event_ignored', {
      reason: 'team_not_in_affected',
      affectedTeamIds: message.affectedTeamIds,
    });
    return false;
  }
  return true;
}

export function dispatchQueueRealtimeGlobalEvent(message) {
  if (!isQueueRealtimeRelevantForCurrentUser(message)) {
    return false;
  }
  const pathNow = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  window.dispatchEvent(
    new CustomEvent('hd:queue-realtime-global-event', {
      detail: {
        source: 'queue-realtime',
        eventType: message.eventType,
        eventId: message.eventId,
        tenantId: message.tenantId,
        ticketId: message.ticketId,
        affectedTeamIds: message.affectedTeamIds,
        path: pathNow,
      },
    })
  );
  logStructured('log', 'queue_global_event_dispatched', {
    eventType: message.eventType,
    eventId: message.eventId,
    ticketId: message.ticketId,
  });
  return true;
}

/**
 * Entry from WebSocket handler after payload shape is validated.
 */
export function handleQueueRealtimeWebSocketMessage(message) {
  if (!QUEUE_REALTIME_SYNC_ENABLED) {
    logStructured('log', 'queue_event_ignored', { reason: 'feature_disabled' });
    return;
  }
  if (!isQueueRealtimePayload(message)) {
    logStructured('log', 'queue_event_ignored', { reason: 'not_queue_payload' });
    return;
  }

  logStructured('log', 'websocket_queue_event_received', {
    eventType: message.eventType,
    eventId: message.eventId,
    tenantId: message.tenantId,
    ticketId: message.ticketId,
    affectedTeamIds: message.affectedTeamIds,
  });

  const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  if (!isQueueRelatedPath(path)) {
    logStructured('log', 'queue_event_ignored', { reason: 'not_queue_screen', path });
    return;
  }

  if (!tenantMatchesPayload(message)) {
    return;
  }

  if (!userTouchesAffectedTeams(message)) {
    logStructured('log', 'queue_event_ignored', {
      reason: 'team_not_in_affected',
      affectedTeamIds: message.affectedTeamIds,
    });
    return;
  }

  logStructured('log', 'queue_event_accepted', {
    eventType: message.eventType,
    ticketId: message.ticketId,
    path,
  });

  if (debounceTimer != null) {
    logStructured('log', 'debounce_suppressed', {
      eventType: message.eventType,
      debounceMs: DEBOUNCE_MS,
    });
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  debounceGeneration += 1;
  const gen = debounceGeneration;

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    if (gen !== debounceGeneration) {
      return;
    }

    const pathNow = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    if (!isQueueRelatedPath(pathNow)) {
      logStructured('log', 'queue_refresh_skipped', { reason: 'left_queue_screen', path: pathNow });
      return;
    }

    const tok = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (!tok) {
      logStructured('log', 'queue_refresh_skipped', { reason: 'no_token' });
      return;
    }

    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    logStructured('log', 'queue_refresh_triggered', {
      eventType: message.eventType,
      ticketId: message.ticketId,
      path: pathNow,
    });

    window.dispatchEvent(
      new CustomEvent('hd:queue-realtime-refresh', {
        detail: {
          source: 'queue-realtime',
          eventType: message.eventType,
          eventId: message.eventId,
          tenantId: message.tenantId,
          ticketId: message.ticketId,
          affectedTeamIds: message.affectedTeamIds,
          path: pathNow,
        },
      })
    );

    const elapsed =
      typeof performance !== 'undefined' ? performance.now() - t0 : 0;
    logStructured('log', 'queue_refresh_dispatched', {
      durationMs: Math.round(elapsed * 100) / 100,
      eventType: message.eventType,
    });
  }, DEBOUNCE_MS);
}
